import { useEffect, useState, type CSSProperties } from 'react';
import type { Rule, RuleParam } from '@soc/shared';
import { RuleIcon } from '../../lib/ruleIcons';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { FREEZE_MS } from '../detection/useDetectionEngine';
import { useRuleControl, type RuleControl } from '../detection/useRuleControl';
import { setRuleParam } from './rulesUiSlice';
import { customWindowEndsNow, customWindowError, RuleWindowChips } from './RuleWindowChips';

interface RuleCardProps {
  rule: Rule;
}

export function RuleCard({ rule }: RuleCardProps) {
  const [hover, setHover] = useState(false);
  const ctrl = useRuleControl(rule.id);
  // Why the custom range can't run (from >= to, starts too far back), or null —
  // gates Start/Fetch (but not dispatch). Same check RuleWindowChips shows inline.
  const customError = customWindowError(ctrl.custom);

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        background: 'var(--surface-card)',
        border: `1px solid ${hover ? 'var(--border-hover)' : 'var(--border-card)'}`,
        borderRadius: 10,
        padding: '6px',
        transition: 'border-color .15s',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 28,
            height: 28,
            flex: 'none',
            color: 'var(--text-primary)',
          }}
        >
          <RuleIcon ruleId={rule.id} size={23} />
        </span>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', lineHeight: 1.25 }}>
          <span style={{ fontSize: 13.5, fontWeight: 600, ...ellipsis }} title={rule.name}>
            {rule.name}
          </span>
          <span
            style={{ fontSize: 12, color: 'var(--text-muted)', ...ellipsis }}
            title={rule.description}
          >
            {rule.description}
          </span>
        </div>
        <StatusBadge status={ctrl.status} count={ctrl.count} />
      </div>

      {rule.params.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          {rule.params.map((param) => (
            <RuleParamInput key={param.key} ruleId={rule.id} param={param} />
          ))}
        </div>
      )}

      <RuleWindowChips
        windowMs={ctrl.windowMs}
        onPick={ctrl.setWindow}
        custom={ctrl.custom}
        onSetCustom={ctrl.setCustomWindow}
        onClearCustom={ctrl.clearCustomWindow}
        disabled={ctrl.status !== 'idle'}
      />

      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        {rule.mode === 'live' ? (
          <LiveControls ctrl={ctrl} customError={customError} />
        ) : (
          <SingleShotControls ctrl={ctrl} customError={customError} />
        )}
      </div>

      {ctrl.error && (
        <div style={{ marginTop: 8, fontSize: 11, color: '#ff4d4d' }}>{ctrl.error}</div>
      )}
    </div>
  );
}

/**
 * Controls for a live rule, driven by its status. A live rule can EITHER run live
 * (Start → Pause/Resume/Stop) OR do a forensic one-shot Fetch (idle → Fetch →
 * Fetching…/Refetch cooldown) — never both at once, since both funnel through the
 * single `status` field. Start additionally needs a window that ends "now"; Fetch
 * is the forensic path and happily runs a past-ending custom window.
 */
function LiveControls({ ctrl, customError }: { ctrl: RuleControl; customError: string | null }) {
  const base = ctrl.canStart && !customError; // project selected + valid window
  const canFetch = base; // forensic: a past-ending window is fine
  const endsNow = customWindowEndsNow(ctrl.custom);
  const canStart = base && endsNow; // live only when the window ends at the present
  const startTitle = !ctrl.canStart
    ? 'Select a project to scope detection before starting'
    : customError
      ? `Custom range invalid: ${customError}`
      : !endsNow
        ? 'Live needs a window that ends now — use Fetch for a past range'
        : undefined;
  const fetchTitle = !ctrl.canStart
    ? 'Select a project to scope detection before fetching'
    : (customError && `Custom range invalid: ${customError}`) || undefined;
  const remaining = useFreezeCountdown(ctrl.status === 'frozen' ? ctrl.lastRunAt : null);

  // Stop is offered once there's something to clear — a live run, an in-flight/cooldown
  // Fetch, or leftover alerts. Clears this rule's alerts and returns it to idle.
  const stopBtn =
    ctrl.status !== 'idle' || ctrl.count > 0 ? (
      <button onClick={ctrl.stop} style={ctrlBtn('stop')}>
        ■ Stop
      </button>
    ) : null;

  if (ctrl.status === 'running') {
    return (
      <>
        <button onClick={ctrl.pause} style={ctrlBtn('pause')}>
          ❚❚ Pause
        </button>
        {stopBtn}
      </>
    );
  }
  if (ctrl.status === 'paused') {
    return (
      <>
        <button onClick={ctrl.start} style={ctrlBtn('start')}>
          ▶ Resume
        </button>
        {stopBtn}
      </>
    );
  }
  if (ctrl.status === 'fetching') {
    return (
      <>
        <button disabled style={ctrlBtn('fetch', true)}>
          ⟳ Fetching…
        </button>
        {stopBtn}
      </>
    );
  }
  if (ctrl.status === 'frozen') {
    return (
      <>
        <button disabled style={ctrlBtn('fetch', true)}>
          Refetch in {remaining}s
        </button>
        {stopBtn}
      </>
    );
  }
  // idle — offer BOTH a live Start and a forensic Fetch
  return (
    <>
      <button
        onClick={ctrl.start}
        disabled={!canStart}
        title={startTitle}
        style={ctrlBtn('start', !canStart)}
      >
        ▶ Start
      </button>
      <button
        onClick={ctrl.fetch}
        disabled={!canFetch}
        title={fetchTitle}
        style={ctrlBtn('fetch', !canFetch)}
      >
        ⤓ Fetch
      </button>
    </>
  );
}

/** One-off Fetch + a 5s freeze cooldown for a single-shot rule. */
function SingleShotControls({ ctrl, customError }: { ctrl: RuleControl; customError: string | null }) {
  const remaining = useFreezeCountdown(ctrl.status === 'frozen' ? ctrl.lastRunAt : null);
  // Stop is offered once there's something to clear: an in-flight/cooldown run,
  // or leftover alerts from a prior fetch. Like the live rules' Stop, it clears
  // this rule's alerts and returns it to idle — here it also ends the cooldown
  // so the operator can immediately refetch.
  const stopBtn =
    ctrl.status !== 'idle' || ctrl.count > 0 ? (
      <button onClick={ctrl.stop} style={ctrlBtn('stop')}>
        ■ Stop
      </button>
    ) : null;

  if (ctrl.status === 'fetching') {
    return (
      <>
        <button disabled style={ctrlBtn('fetch', true)}>
          ⟳ Fetching…
        </button>
        {stopBtn}
      </>
    );
  }
  if (ctrl.status === 'frozen') {
    return (
      <>
        <button disabled style={ctrlBtn('fetch', true)}>
          Refetch in {remaining}s
        </button>
        {stopBtn}
      </>
    );
  }
  const canFetch = ctrl.canStart && !customError;
  const fetchTitle = !ctrl.canStart
    ? 'Select a project to scope detection before fetching'
    : (customError && `Custom range invalid: ${customError}`) || undefined;
  return (
    <>
      <button
        onClick={ctrl.fetch}
        disabled={!canFetch}
        title={fetchTitle}
        style={ctrlBtn('fetch', !canFetch)}
      >
        ⤓ Fetch
      </button>
      {stopBtn}
    </>
  );
}

/** Seconds left in the single-shot cooldown, ticking down from `lastRunAt`. */
function useFreezeCountdown(startedAtIso: string | null): number {
  const [remaining, setRemaining] = useState(() => secondsLeft(startedAtIso));
  useEffect(() => {
    if (!startedAtIso) return;
    setRemaining(secondsLeft(startedAtIso));
    const id = setInterval(() => setRemaining(secondsLeft(startedAtIso)), 250);
    return () => clearInterval(id);
  }, [startedAtIso]);
  return remaining;
}

function secondsLeft(startedAtIso: string | null): number {
  if (!startedAtIso) return 0;
  const elapsed = Date.now() - Date.parse(startedAtIso);
  return Math.max(0, Math.ceil((FREEZE_MS - elapsed) / 1000));
}

/** Compact status pill + per-rule alert count. */
function StatusBadge({ status, count }: { status: RuleControl['status']; count: number }) {
  const meta = STATUS_META[status];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 'none' }}>
      {count > 0 && (
        <span
          className="mono"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--text-secondary)',
            background: 'var(--surface-app)',
            border: '1px solid var(--border)',
            borderRadius: 6,
            padding: '1px 6px',
          }}
        >
          {count}
        </span>
      )}
      <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            background: meta.color,
            boxShadow: meta.glow ? `0 0 7px ${meta.color}` : 'none',
          }}
        />
        <span
          className="eyebrow"
          style={{ fontSize: 10, letterSpacing: 1, color: meta.color }}
        >
          {meta.label}
        </span>
      </span>
    </div>
  );
}

const STATUS_META: Record<RuleControl['status'], { label: string; color: string; glow: boolean }> = {
  idle: { label: 'IDLE', color: 'var(--text-dim)', glow: false },
  running: { label: 'LIVE', color: 'var(--ok)', glow: true },
  paused: { label: 'PAUSED', color: '#e6b43c', glow: false },
  fetching: { label: 'FETCHING', color: 'var(--accent)', glow: true },
  frozen: { label: 'COOLDOWN', color: 'var(--accent)', glow: false },
};

/** Single-line truncation for the header name/description so a narrow sidebar
 *  ellipsises them instead of wrapping and distorting the card. */
const ellipsis: CSSProperties = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
};

/** Control-button chrome (not severity — literals mirror the old header control). */
function ctrlBtn(variant: 'start' | 'pause' | 'stop' | 'fetch', disabled = false): CSSProperties {
  const palette: Record<typeof variant, { bg: string; border: string; color: string }> = {
    start: { bg: 'rgba(41,224,168,0.1)', border: 'rgba(41,224,168,0.3)', color: 'var(--ok)' },
    fetch: { bg: 'rgba(0,229,255,0.08)', border: 'rgba(0,229,255,0.32)', color: 'var(--accent)' },
    pause: { bg: 'rgba(230,180,60,0.1)', border: 'rgba(230,180,60,0.32)', color: '#e6b43c' },
    stop: { bg: 'rgba(255,77,77,0.1)', border: 'rgba(255,77,77,0.32)', color: '#ff4d4d' },
  };
  const p = palette[variant];
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    // flex-basis:0 + minWidth:0 lets the two buttons split the card evenly and
    // keep shrinking on a narrow sidebar instead of overflowing their labels.
    flex: '1 1 0',
    minWidth: 0,
    justifyContent: 'center',
    whiteSpace: 'nowrap',
    background: p.bg,
    border: `1px solid ${p.border}`,
    borderRadius: 8,
    color: p.color,
    cursor: disabled ? 'not-allowed' : 'pointer',
    font: 'inherit',
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.5,
    opacity: disabled ? 0.5 : 1,
    padding: '6px 8px',
  };
}

/** One editable rule input (e.g. the MCC rule's "Home MCC"). */
function RuleParamInput({ ruleId, param }: { ruleId: string; param: RuleParam }) {
  const dispatch = useAppDispatch();
  const value = useAppSelector((s) => s.rulesUi.params[ruleId]?.[param.key]) ?? param.default;
  const isNumber = param.type === 'number';

  return (
    <label
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
        fontSize: 12,
        color: 'var(--text-muted)',
      }}
    >
      {param.label}
      <input
        type={isNumber ? 'number' : 'text'}
        className="mono"
        value={value}
        min={param.min}
        max={param.max}
        onChange={(e) =>
          dispatch(
            setRuleParam({
              ruleId,
              key: param.key,
              value: isNumber ? Number(e.target.value) : e.target.value,
            }),
          )
        }
        style={{
          // Cap at the natural width but allow shrinking (min-width:0) so the card
          // never forces its grid track wider than the drag-set sidebar width.
          width: isNumber ? 64 : 110,
          maxWidth: '100%',
          minWidth: 0,
          background: 'var(--surface-app)',
          border: '1px solid var(--border)',
          borderRadius: 6,
          color: 'var(--text-primary)',
          font: 'inherit',
          fontSize: 12,
          padding: '3px 7px',
        }}
      />
    </label>
  );
}
